import { createAsyncThunk } from '@reduxjs/toolkit';
import { getScopeReadyData } from 'src/state/scope/Scope.types';
import type { ThunkApi } from 'src/store.types';
import { getMfpModule } from 'src/pages/NavigationShell/navigationUtils';
import type { AgentConversation, AgentChatMessage } from './Agent.types';
import { getActivePerspectiveAgentButtonConfig } from 'src/pages/PerspectiveSelection/PerspectiveSelection.selectors';
import type { AgentActionGroup, AgentConfig } from 'configuration-schemas/viewdefns/viewdefn';
import { zAgentConfig } from 'configuration-schemas/viewdefns/viewdefn';
import { ASSORTMENT } from 'src/utils/Domain/Constants';

export const addChat = createAsyncThunk<
  { convoId: string; message: AgentChatMessage },
  { convoId: string; message: AgentChatMessage },
  ThunkApi & { rejectValue: string }
>('agent/addChat', async ({ convoId, message }, { getState, rejectWithValue, extra }) => {
  const agentService = extra.agent;

  try {
    const response = await agentService.addChat(convoId, message);
    return { convoId, message };
  } catch (err) {
    return rejectWithValue((err as Error).message);
  }
});

export const getChats = createAsyncThunk<AgentConversation[], undefined, ThunkApi & { rejectValue: string }>(
  'agent/getChats',
  async (_payload, { getState, rejectWithValue, extra }) => {
    const agentService = extra.agent;

    try {
      return await agentService.getChats('session_here_if_we_add_it');
    } catch (err) {
      return rejectWithValue((err as Error).message);
    }
  },
);

export const createChat = createAsyncThunk<{ convoId: string }, undefined, ThunkApi & { rejectValue: string }>(
  'agent/createChat',
  async (_payload, { getState, rejectWithValue, extra }) => {
    const agentService = extra.agent;

    try {
      return await agentService.createChat();
    } catch (err) {
      return rejectWithValue((err as Error).message);
    }
  },
);

export const getChat = createAsyncThunk<{ message: string }[], string, ThunkApi & { rejectValue: string }>(
  'agent/getChat',
  async (convoId, { getState, rejectWithValue, extra }) => {
    const agentService = extra.agent;

    try {
      return await agentService.getChat(convoId);
    } catch (err) {
      return rejectWithValue((err as Error).message);
    }
  },
);

export const getAgentConfig = createAsyncThunk<AgentConfig, undefined, ThunkApi & { rejectValue: string }>(
  'agent/getAgentConfig',
  async (_payload, { getState, rejectWithValue, extra }) => {
    const state = getState();
    const agentButtonConfig = getActivePerspectiveAgentButtonConfig(state);
    const { tenantConfigClient } = extra;

    if (!agentButtonConfig?.configApi) {
      return rejectWithValue('Agent button config or configApi not found');
    }

    const { configApi } = agentButtonConfig;

    try {
      const configs = await tenantConfigClient.getTenantViewDefns<AgentConfig>({
        defnIds: [configApi.params.defnId || ''],
        appName: configApi.params.appName,
        validationSchemas: [zAgentConfig],
      });

      return configs[0];
    } catch (err) {
      return rejectWithValue((err as Error).message);
    }
  },
);
